import React from 'react';
import { useNavigate } from 'react-router-dom';

interface State {
  hasError: boolean;
  error: Error | null;
}

interface Props {
  children: React.ReactNode;
}

class ErrorBoundaryClass extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  reset = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      return <ErrorFallback error={this.state.error} onReset={this.reset} />;
    }
    return this.props.children;
  }
}

const ErrorFallback = ({
  error,
  onReset,
}: {
  error: Error | null;
  onReset: () => void;
}) => {
  const navigate = useNavigate();

  const handleGoHome = () => {
    onReset();
    navigate('/');
  };

  const handleRetry = () => {
    onReset();
  };

  return (
    <div className="flex flex-col items-center justify-center h-full min-h-[400px] px-6 font-header">
      <div className="flex flex-col items-center gap-6 max-w-md text-center">
        <div className="w-16 h-16 rounded-full bg-[#FFF0F0] flex items-center justify-center">
          <svg
            width="32"
            height="32"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#E5484D"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
        </div>

        <div className="flex flex-col gap-2">
          <h2 className="text-xl font-semibold text-[#191B1C]">
            Something went wrong
          </h2>
          <p className="text-sm text-[#9B919D] leading-relaxed">
            An unexpected error occurred while loading this page. Try refreshing
            or go back to the dashboard.
          </p>
          {process.env.NODE_ENV === 'development' && error?.message && (
            <p className="text-xs text-[#E5484D] bg-[#FFF0F0] rounded-lg px-3 py-2 mt-1 text-left font-mono break-all">
              {error.message}
            </p>
          )}
        </div>

        <div className="flex gap-3">
          <button
            onClick={handleGoHome}
            className="px-4 py-2 text-sm font-medium text-[#9B919D] border border-[#E5E9EB] rounded-lg hover:bg-[#F9F9F9] transition-colors"
          >
            Go to dashboard
          </button>
          <button
            onClick={handleRetry}
            className="px-4 py-2 text-sm font-medium text-white bg-[#0FAF62] rounded-lg hover:bg-[#0d9e58] transition-colors"
          >
            Try again
          </button>
        </div>
      </div>
    </div>
  );
};

export default ErrorBoundaryClass;
